import { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, CheckCircle2, AlertCircle, Download, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_CSV = `student_id,first_name,last_name,grade_level,email,driveline_tag,barcode
100001,Emma,Johnson,7,emma.johnson@iltexasstudent.org,412,BC100001
100002,Liam,Garcia,6,liam.garcia@iltexasstudent.org,213,BC100002
100003,Olivia,Martinez,8,olivia.martinez@iltexasstudent.org,588,BC100003
`;

export default function UploadStudents() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (f) => {
    setResult(null);
    setError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Please choose a .csv file.');
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/students/bulk-upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_roster_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
            <Users className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Upload Student Roster</h1>
            <p className="text-slate-600 mt-1">
              Bulk-add students so they can sign in with their student ID.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              CSV Format
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Your CSV should have these column headers (order does not matter):
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100">student_id (required)</Badge>
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100">first_name (required)</Badge>
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100">last_name (required)</Badge>
              <Badge variant="secondary">grade_level</Badge>
              <Badge variant="secondary">email</Badge>
              <Badge variant="secondary">driveline_tag</Badge>
              <Badge variant="secondary">barcode</Badge>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-600 mb-2">Every uploaded student gets the default password:</p>
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border">E@gles26!</code>
              <p className="text-xs text-slate-500 mt-2">
                Students are required to change their password on first sign in.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadSample}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV template
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Upload CSV</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer transition-all"
            >
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="font-medium text-slate-700">
                {file ? file.name : 'Drop your CSV here or click to browse'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : 'Max 5 MB. Duplicates by student_id are skipped.'}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                onClick={upload}
                disabled={!file || uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {uploading ? 'Uploading...' : 'Upload Roster'}
              </Button>
              {file && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                    setError('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {result && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  <p className="font-semibold">Upload complete</p>
                  <p className="text-sm mt-1">
                    Added <strong>{result.added}</strong> students, skipped{' '}
                    <strong>{result.skipped}</strong>.
                  </p>
                  {result.errors?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-slate-700">
                        {result.errors.length} error{result.errors.length === 1 ? '' : 's'} - click
                        to view
                      </summary>
                      <ul className="mt-2 space-y-0.5 list-disc list-inside text-red-800">
                        {result.errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
